import React, { useState, Children } from 'react';
import { Card } from './ui/Card';
import { Button } from './ui/Button';
import { OwnershipSummary } from './OwnershipSummary';
import { OwnershipTable } from './OwnershipTable';
import { OwnershipModal } from './OwnershipModal';
import { LoanSelectionModal } from './LoanSelectionModal';
import { useOwnershipData } from '../hooks/useOwnershipData';
import { CURRENT_ENTITY, NON_MEMBER_SIGNERS, ENTITY_MEMBER_CHILDREN } from '../data/mockData';
import { Plus, History, Building2, AlertTriangle, Save } from 'lucide-react';
import { OwnershipRecord } from '../types/ownership';
// Small inline data point for the entity header
function EntityDataPoint({
  label,
  value,
  mono = false
}: {
  label: string;
  value: React.ReactNode;
  mono?: boolean;
}) {
  return <div className="flex flex-col" data-id="element-72">
      <span className="text-[10px] font-bold uppercase tracking-wider text-[#706e6b]" data-id="element-73">
        {label}
      </span>
      <span className={`text-sm text-[#080707] font-medium mt-0.5 ${mono ? 'font-mono' : ''}`} data-id="element-74">
        {value}
      </span>
    </div>;
}
export function OwnershipDashboard() {
  const {
    records,
    pendingChanges,
    hasPendingChanges,
    totalOwnership,
    primaryMemberCount,
    showHistorical,
    setShowHistorical,
    addRecord,
    updateRecord,
    deleteRecord,
    applyPendingChanges,
    discardPendingChanges
  } = useOwnershipData();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoanModalOpen, setIsLoanModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState<OwnershipRecord | undefined>(undefined);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const handleAddNew = () => {
    setEditingRecord(undefined);
    setIsModalOpen(true);
  };
  const handleEdit = (record: OwnershipRecord) => {
    setEditingRecord(record);
    setIsModalOpen(true);
  };
  const handleDelete = (id: string) => {
    const isPendingAdd = pendingChanges.some(c => c.type === 'add' && c.record.id === id);
    if (isPendingAdd || window.confirm('Are you sure you want to delete this future-dated record?')) {
      const result = deleteRecord(id);
      if (result && !result.success) {
        setDeleteError(result.error || 'Failed to delete');
        setTimeout(() => setDeleteError(null), 3000);
      }
    }
  };
  const handleSave = (recordData: Omit<OwnershipRecord, 'id' | 'createdDate'>) => {
    if (editingRecord) return updateRecord(editingRecord.id, recordData);else return addRecord(recordData);
  };
  const handleApplyChanges = (selectedLoanIds: string[]) => {
    console.log('Applying changes to loans:', selectedLoanIds);
    applyPendingChanges();
    setIsLoanModalOpen(false);
  };
  const getPendingChangesSummary = () => {
    const adds = pendingChanges.filter(c => c.type === 'add').length;
    const edits = pendingChanges.filter(c => c.type === 'edit').length;
    const deletes = pendingChanges.filter(c => c.type === 'delete').length;
    const parts = [];
    if (adds > 0) parts.push(`${adds} added`);
    if (edits > 0) parts.push(`${edits} edited`);
    if (deletes > 0) parts.push(`${deletes} deleted`);
    return parts.join(', ');
  };
  const cardActions = hasPendingChanges ? <div className="flex items-center gap-3" data-id="element-75">
      <div className="flex items-center gap-2 px-3 py-1.5 bg-[#fff8c5] border border-[#d4a72c] rounded-[4px]" data-id="element-76">
        <AlertTriangle className="w-4 h-4 text-[#8e5b04]" data-id="element-77" />
        <span className="text-xs font-medium text-[#3e3e3c]" data-id="element-78">
          {pendingChanges.length} unsaved: {getPendingChangesSummary()}
        </span>
      </div>
      <Button variant="neutral" size="sm" onClick={discardPendingChanges} data-id="element-79">
        Discard
      </Button>
      <Button variant="primary" size="sm" onClick={() => setIsLoanModalOpen(true)} leftIcon={<Save className="w-4 h-4" data-id="element-81" />} data-id="element-80">
        Save Changes
      </Button>
    </div> : null;
  return <div className="relative" data-id="element-82">
      <div className="max-w-6xl mx-auto p-6 space-y-6" data-id="element-83">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-start justify-between gap-4" data-id="element-84">
          <div className="flex items-start gap-3" data-id="element-85">
            <div className="p-3 bg-white border border-[#dddbda] rounded-[4px] shadow-sm mt-0.5" data-id="element-86">
              <Building2 className="w-6 h-6 text-[#0176d3]" data-id="element-87" />
            </div>
            <div data-id="element-88">
              <h1 className="text-xl font-bold text-[#080707]" data-id="element-89">
                {CURRENT_ENTITY.name}
              </h1>
              <p className="text-sm text-[#706e6b] mb-3" data-id="element-90">
                Entity Ownership Management
              </p>

              {/* Entity data points */}
              <div className="flex flex-wrap items-center gap-x-5 gap-y-2" data-id="element-91">
                <EntityDataPoint label="EIN" value={CURRENT_ENTITY.ein ?? <span className="text-[#706e6b] font-normal" data-id="element-93">
                        Not on record
                      </span>} mono data-id="element-92" />
                <div className="w-px h-7 bg-[#dddbda] hidden sm:block" data-id="element-94" />
                <EntityDataPoint label="State of Registration" value={CURRENT_ENTITY.businessIncorporateState ?? <span className="text-[#706e6b] font-normal" data-id="element-96">
                        Not on record
                      </span>} data-id="element-95" />
                <div className="w-px h-7 bg-[#dddbda] hidden sm:block" data-id="element-97" />
                <EntityDataPoint label="Foreign Entity Registration" value={CURRENT_ENTITY.foreignEntityRegistration ? <span className="inline-flex items-center gap-1" data-id="element-99">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#fe9339] inline-block" data-id="element-100" />
                        {CURRENT_ENTITY.foreignEntityRegistration}
                      </span> : CURRENT_ENTITY.foreignEntityRegistration === null ? <span className="text-[#706e6b] font-normal" data-id="element-101">
                        Not applicable
                      </span> : <span className="text-[#706e6b] font-normal" data-id="element-102">
                        Not on record
                      </span>} data-id="element-98" />
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3 flex-shrink-0" data-id="element-103">
            <Button variant={showHistorical ? 'primary' : 'neutral'} onClick={() => setShowHistorical(!showHistorical)} leftIcon={<History className="w-4 h-4" data-id="element-105" />} data-id="element-104">
              {showHistorical ? 'Hide History' : 'Show History'}
            </Button>
            <Button variant="primary" onClick={handleAddNew} leftIcon={<Plus className="w-4 h-4" data-id="element-107" />} data-id="element-106">
              Add Ownership
            </Button>
          </div>
        </div>

        {deleteError && <div className="bg-[#ba0517] text-white px-4 py-3 rounded-[4px] text-sm font-medium" data-id="element-108">
            {deleteError}
          </div>}

        {/* Summary Cards */}
        <OwnershipSummary totalPercentage={totalOwnership} primaryMemberCount={primaryMemberCount} businessIncorporateState={CURRENT_ENTITY.businessIncorporateState} data-id="element-109" />

        {/* Main Content */}
        <Card title="Ownership Records" actions={cardActions} className="min-h-[400px]" data-id="element-110">
          <OwnershipTable records={records} onEdit={handleEdit} onDelete={handleDelete} isHistoricalView={showHistorical} pendingChanges={pendingChanges} nonMemberSigners={NON_MEMBER_SIGNERS} entityMemberChildren={ENTITY_MEMBER_CHILDREN} data-id="element-111" />
        </Card>

        {/* Ownership Modal */}
        <OwnershipModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} onSave={handleSave} initialData={editingRecord} entityId={CURRENT_ENTITY.id} data-id="element-112" />

        {/* Loan Selection Modal */}
        <LoanSelectionModal isOpen={isLoanModalOpen} onClose={() => setIsLoanModalOpen(false)} onConfirm={handleApplyChanges} pendingChanges={pendingChanges} data-id="element-113" />
      </div>
    </div>;
}